import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Printer, Eye, FileDown, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatTime } from "@/lib/dates";
import { fetchRegistrationTeamNames } from "@/lib/registrationTeamNames";

/**
 * RLE Court Schedule sheet.
 * Layout IDENTICAL to RL's CourtScheduleDrawer: one court per printed page, all days listed,
 * navy court header, Time / Matchup / Status table per day.
 * Rewired to RLE: scheduled_games (event's published/latest schedule_version) + event_courts.
 * RL branding removed.
 */

interface CourtScheduleDrawerProps {
  eventId: string;
  open: boolean;
  onClose: () => void;
}

interface GameRow {
  id: string;
  scheduled_date: string | null;       // YYYY-MM-DD
  scheduled_start_time: string | null;  // HH:MM:SS
  court_name: string;
  team_a_name: string;
  team_b_name: string;
  game_status: string | null;
}

const CourtScheduleDrawer: React.FC<CourtScheduleDrawerProps> = ({ eventId, open, onClose }) => {
  const [games, setGames] = useState<GameRow[]>([]);
  const [courtOptions, setCourtOptions] = useState<string[]>([]);
  const [selectedCourts, setSelectedCourts] = useState<Set<string>>(new Set());
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !eventId) return;
    const load = async () => {
      setLoading(true);

      const { data: ev } = await supabase
        .from('events')
        .select('name, published_schedule_version_id')
        .eq('id', eventId)
        .maybeSingle();
      setEventName(ev?.name || 'Event');

      let versionId = ev?.published_schedule_version_id as string | null;
      if (!versionId) {
        const { data: ver } = await supabase
          .from('schedule_versions')
          .select('id')
          .eq('event_id', eventId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        versionId = ver?.id || null;
      }
      if (!versionId) { setGames([]); setLoading(false); return; }

      const { data: rawGames } = await supabase
        .from('scheduled_games')
        .select('id, scheduled_date, scheduled_start_time, game_status, team_a_registration_id, team_b_registration_id, court_id')
        .eq('schedule_version_id', versionId)
        .order('scheduled_date')
        .order('scheduled_start_time')
        .order('sort_order');

      // Resolve court names via separate lookup (avoids flaky PostgREST embed 404s)
      const courtNameById = new Map<string, string>();
      const { data: courtRows } = await supabase
        .from('event_courts')
        .select('id, name')
        .eq('event_id', eventId);
      (courtRows || []).forEach((c: any) => courtNameById.set(c.id, c.name));

      const playable = (rawGames || []).filter((g: any) => !['cancelled', 'bye'].includes(g.game_status));

      // Team names come from the registration behind each game.
      const nameByRegId = await fetchRegistrationTeamNames(
        playable.flatMap((g: any) => [g.team_a_registration_id, g.team_b_registration_id]),
      );
      const nameFor = (regId: string | null) => (regId && nameByRegId[regId]) || 'TBD';

      const rows: GameRow[] = playable.map((g: any) => ({
        id: g.id,
        scheduled_date: g.scheduled_date,
        scheduled_start_time: g.scheduled_start_time,
        court_name: (g.court_id && courtNameById.get(g.court_id)) || 'Unassigned',
        team_a_name: nameFor(g.team_a_registration_id),
        team_b_name: nameFor(g.team_b_registration_id),
        game_status: g.game_status,
      }));
      setGames(rows);

      const allCourts = [...new Set(rows.map(r => r.court_name))].sort();
      setCourtOptions(allCourts);
      setSelectedCourts(new Set(allCourts));
      setLoading(false);
    };
    load();
  }, [open, eventId]);

  const allSelected = selectedCourts.size === courtOptions.length && courtOptions.length > 0;
  const toggleAll = () => setSelectedCourts(allSelected ? new Set() : new Set(courtOptions));
  const toggleCourt = (court: string) => setSelectedCourts(prev => {
    const next = new Set(prev); next.has(court) ? next.delete(court) : next.add(court); return next;
  });

  const filteredGames = useMemo(() => games.filter(g => selectedCourts.has(g.court_name)), [games, selectedCourts]);

  // Group: Court -> Day -> games
  const grouped = useMemo(() => {
    const g: Record<string, Record<string, GameRow[]>> = {};
    filteredGames.forEach(row => {
      const day = row.scheduled_date || 'TBD';
      (g[row.court_name] ||= {});
      (g[row.court_name][day] ||= []).push(row);
    });
    return g;
  }, [filteredGames]);

  const sortedCourts = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  const fmtTime = (t: string | null) => formatTime(t, '—');
  const fmtDate = (d: string) => {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return d;
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Court Schedule - ${eventName}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; }
        .court-page { page-break-after: always; }
        .court-page:last-child { page-break-after: auto; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
    `);
    printWindow.document.write(el.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="sm:max-w-md flex flex-col overflow-hidden">
          <SheetHeader><SheetTitle>Court Schedule</SheetTitle></SheetHeader>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Select Courts</Label>
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={toggleAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto border rounded-md p-2">
                  <div className="space-y-1">
                    {courtOptions.map(court => (
                      <label key={court} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                        <Checkbox checked={selectedCourts.has(court)} onCheckedChange={() => toggleCourt(court)} />
                        <span>{court}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedCourts.size} court{selectedCourts.size !== 1 ? 's' : ''} selected</Badge>
                <Badge variant="outline">{filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">Each selected court prints on its own page with all days listed.</p>

              <div className="flex gap-2">
                <Button onClick={() => setShowPreview(true)} disabled={filteredGames.length === 0} className="flex-1">
                  <Eye className="h-4 w-4 mr-2" /> Preview
                </Button>
              </div>

              {games.length === 0 && (
                <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">No scheduled games found for this event.</p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="flex items-center justify-between">
              <span>Court Schedule — {eventName}</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={handlePrint}><Printer className="h-4 w-4 mr-1" /> Print</Button>
                <Button size="sm" variant="outline" onClick={handlePrint}><FileDown className="h-4 w-4 mr-1" /> Download PDF</Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef}>
            {sortedCourts.map((court, courtIdx) => {
              const daysForCourt = grouped[court];
              const sortedDays = Object.keys(daysForCourt).sort((a, b) => a.localeCompare(b));
              return (
                <div key={court} className="court-page" style={{ pageBreakAfter: courtIdx < sortedCourts.length - 1 ? 'always' : 'auto' }}>
                  <div style={{ background: '#002E62', color: '#fff', padding: '8px 12px', fontSize: '16px', fontWeight: 'bold' }}>
                    🏀 {court}
                    <div style={{ fontSize: '10px', fontWeight: 'normal', opacity: 0.7, marginTop: '2px' }}>{eventName}</div>
                  </div>
                  {sortedDays.map(day => {
                    const dayGames = daysForCourt[day].sort((a, b) => (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || ''));
                    return (
                      <div key={day} style={{ marginBottom: '12px' }}>
                        <div style={{ background: '#e5e7eb', padding: '4px 12px', fontWeight: 600, fontSize: '12px', borderBottom: '1px solid #d1d5db', marginTop: '4px' }}>
                          {day === 'TBD' ? 'Day TBD' : fmtDate(day)}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '3px 8px', fontWeight: 600, borderBottom: '1px solid #d1d5db', fontSize: '10px', width: '70px' }}>Time</th>
                              <th style={{ textAlign: 'left', padding: '3px 8px', fontWeight: 600, borderBottom: '1px solid #d1d5db', fontSize: '10px' }}>Matchup</th>
                              <th style={{ textAlign: 'center', padding: '3px 8px', fontWeight: 600, borderBottom: '1px solid #d1d5db', fontSize: '10px', width: '80px' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dayGames.map(g => (
                              <tr key={g.id}>
                                <td style={{ padding: '3px 8px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', color: '#6b7280', fontSize: '11px' }}>{fmtTime(g.scheduled_start_time)}</td>
                                <td style={{ padding: '3px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 500, fontSize: '11px' }}>
                                  {g.team_a_name} <span style={{ fontWeight: 'normal', color: '#9ca3af' }}>vs</span> {g.team_b_name}
                                </td>
                                <td style={{ padding: '3px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '10px' }}>
                                  {g.game_status === 'completed' ? '✓' : g.game_status === 'in_progress' ? 'Live' : (g.game_status || 'Scheduled')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourtScheduleDrawer;
